import { HttpResponse } from '@angular/common/http';
import { AbstractControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { PhonePrefix } from '@app/models';
import { TranslateService } from '@ngx-translate/core';
import CryptoES from 'crypto-es/lib/index';
import { environment } from 'environments/environment';
import { saveAs } from 'file-saver';
import { importSPKI, jwtVerify } from 'jose';
import { CountryCode, isValidPhoneNumber } from 'libphonenumber-js';
import { Subscription } from 'rxjs';
import { v4 } from 'uuid';
import { FormErr, FormVal, GenericObject } from './types';

export namespace UUID {
  /**
   * @return {*} `random generated v4 uuid`
   */
  export const randomV4 = (): string => {
    return v4();
  };
}

export namespace JWTUtils {
  export const getServerPublicKey = () => {
    return importSPKI(environment.JWT_PUBLIC_KEY!, 'RS256');
  };

  export const extractFromAuthorization = (authorization: string, prefix?: 'Basic' | 'Bearer') => {
    if (!prefix) return authorization;
    if (!authorization.includes(prefix)) return authorization;
    const [, jwt] = authorization.split(`${prefix} `);
    return jwt;
  };

  export const verify = async (token: string) => {
    const publicKey = await JWTUtils.getServerPublicKey();
    return jwtVerify(token, publicKey);
  };
}

export namespace CryptUtils {
  /**
   * Create and returns a Hash object with given algoritm
   * @param {'sha256'} algorithm
   */
  export const createHash = (algorithm: 'SHA1' | 'SHA256') => {
    return CryptoES?.[algorithm];
  };

  /**
   * This method hash the given string in sha256
   * @param {string} str
   * @returns hashed string
   */
  export const hash = (str: string) => {
    const hash = createHash('SHA256');
    return hash(str).toString(CryptoES?.enc.Hex);
  };

  export const encodeBase64 = (str: string) => {
    return window.btoa(str);
  };

  export const decodeBase64 = (str: string) => {
    return window.atob(str);
  };
}

export namespace FormUtils {
  interface AllValidationErrors {
    controlName: string;
    errorName: string;
  }

  interface FormGroupControls {
    [key: string]: AbstractControl;
  }

  const trimControls = (form: FormGroup) => {
    for (let [key, value] of Object.entries(form.value)) {
      if (form.controls[key].errors?.['mismatch']) continue;
      if (typeof value === 'string') {
        form.patchValue({ [key]: value.trim() }, { emitEvent: false, onlySelf: true });
      }
    }
  };

  const getFormValidationErrors = (controls: FormGroupControls, skipPristine: boolean): AllValidationErrors[] => {
    let errors: AllValidationErrors[] = [];
    Object.keys(controls).forEach((key) => {
      const control = controls[key];
      const hasCustomError = Object.keys(control.errors ?? {}).some((error) => error.startsWith('__'));
      if (!skipPristine) {
        control.markAsDirty();
        control.markAsTouched();
        if (!hasCustomError) control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
      }
      if (control.pristine && skipPristine) return;
      if (control instanceof FormGroup) {
        errors = errors.concat(getFormValidationErrors(control.controls, skipPristine));
      }
      const controlErrors: ValidationErrors = control.errors!;
      if (controlErrors !== null) {
        Object.keys(controlErrors).forEach((keyError) => {
          errors.push({
            controlName: key,
            errorName: keyError,
          });
        });
      }
    });
    return errors;
  };

  /**
   * Method to validate the given form and to trim each field.
   * At submit, default value of skipPristine and skipTrim are correct,
   * in subscribe of valueChanges pass skipPristine **true** and trim **false**
   * @param {FormErr} formErr
   * @param {FormVal} formVal
   * @param {FormGroup} form
   * @param {boolean} skipPristine *default false*
   * @param {boolean} trim *default true*
   * @returns if form is valid or not
   */
  export const checkForm = (formErr: FormErr, formVal: FormVal, form: FormGroup, skipPristine: boolean = false, trim: boolean = true): boolean => {
    for (const elem in formErr) {
      formErr[elem] = '';
    }
    if (trim) trimControls(form);
    const controls = form.controls;
    const errors = getFormValidationErrors(controls, skipPristine);
    errors.forEach((error) => {
      const controlName = error.controlName;
      formErr[controlName] = formVal[controlName][error.errorName];
    });
    return !errors.length;
  };

  /**
   * Enable all form controls
   * @param {FormGroupControls} formControls
   * @param {string[]} controlsToCheck
   */
  export const enableFormControls = (formControls: FormGroupControls, controlsToCheck: string[] = []) => {
    if (controlsToCheck.length) {
      for (const control of Object.keys(formControls)) {
        if (controlsToCheck.includes(control)) {
          formControls[control].enable();
          formControls[control].updateValueAndValidity();
        }
      }
    } else {
      for (const control of Object.keys(formControls)) {
        formControls[control].enable();
        formControls[control].updateValueAndValidity();
      }
    }
  };

  /**
   * Disable all form controls
   * @param {FormGroupControls} formControls
   * @param {string[]} controlsToCheck
   */
  export const disableFormControls = (formControls: FormGroupControls, controlsToCheck: string[] = []) => {
    if (controlsToCheck.length) {
      for (const control of Object.keys(formControls)) {
        if (controlsToCheck.includes(control)) {
          formControls[control].disable();
          formControls[control].updateValueAndValidity();
        }
      }
    } else {
      for (const control of Object.keys(formControls)) {
        formControls[control].disable();
        formControls[control].updateValueAndValidity();
      }
    }
  };

  /**
   * Remove all validators from given control
   * @param {AbstractControl} control
   */
  export const removeValidators = (control: AbstractControl) => {
    control.clearValidators();
    control.updateValueAndValidity();
  };

  /**
   * Add given validator to given control
   * @param {AbstractControl} control
   * @param {ValidatorFn} validator
   */
  export const addValidator = (control: AbstractControl, validator: ValidatorFn) => {
    control.setValidators(validator);
    control.updateValueAndValidity();
  };

  /**
   * Checks whether the given phone number is valid or not
   * @param phoneNumber
   * @param prefix
   */
  export const validatePhoneNumber = (phoneNumber: string, prefix: PhonePrefix | null | undefined) => {
    if (!prefix || !phoneNumber) return false;
    const completeNumber = prefix.value + phoneNumber;
    return isValidPhoneNumber(completeNumber, prefix.country as CountryCode);
  };

  /**
   * Returns the maxLength validator with a default value of 50 that can be overridden
   */
  export const maxLengthValidator = (max: number = 50): ValidatorFn => {
    return Validators.maxLength(max);
  };

  /**
   * @returns Object containing all the translations for the p-password component
   */
  export const getPasswordHelper = (translate: TranslateService): GenericObject => {
    return {
      enterPassword: translate.instant('COMPONENTS.PASSWORD.ENTER_PASSWORD'),
      suggestion: translate.instant('COMPONENTS.PASSWORD.SUGGESTIONS'),
      weak: translate.instant('COMPONENTS.PASSWORD.WEAK'),
      medium: translate.instant('COMPONENTS.PASSWORD.MEDIUM'),
      strong: translate.instant('COMPONENTS.PASSWORD.STRONG'),
      lowerCase: translate.instant('COMPONENTS.PASSWORD.LOWERCASE'),
      upperCase: translate.instant('COMPONENTS.PASSWORD.UPPERCASE'),
      numeric: translate.instant('COMPONENTS.PASSWORD.NUMERIC'),
      symbol: translate.instant('COMPONENTS.PASSWORD.SYMBOL'),
      minLength: translate.instant('COMPONENTS.PASSWORD.MIN_LENGTH'),
      maxLength: translate.instant('COMPONENTS.PASSWORD.MAX_LENGTH'),
    };
  };
}

export namespace Utils {
  /**
   * Cycle through given array of subscriptions and unsubscribe from all
   * @param {Subscription[]} subscriptions
   * @returns empty array of subscriptions
   */
  export const unsubscribe = (subscriptions: Subscription[]): Subscription[] => {
    for (const sub of subscriptions) {
      sub.unsubscribe();
    }
    return [];
  };

  /**
   * Given an image returns the same image in base64.
   * @param {File} file
   * @param {boolean} rawBase64
   * @returns encoded image in base64
   */
  export const fileToDataUrl = (file: File | Blob, rawBase64: boolean = false): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const encoded = String(reader.result);
        if (rawBase64) {
          let raw = encoded.replace(/^data:(.*;base64,)?/, '');
          if (raw.length % 4 > 0) {
            raw += '='.repeat(4 - (raw.length % 4));
          }
          resolve(raw);
          return;
        }
        resolve(encoded);
      };
      reader.onerror = reject;
    });
  };

  /**
   * Given string in base64 and its type return its dataUrl
   * @param {string} rawBase64
   * @param {string} type
   * @returns dataUrl
   */
  export const base64ToDataUrl = (rawBase64: string | undefined, type: string = 'png'): string | null => {
    if (!rawBase64) return null;
    return `data:image/${type};base64,` + rawBase64;
  };

  /**
   * Given a dataUrl returns the raw base64 string
   * @param {string | null} dataUrl
   * @returns raw base64
   */
  export const getRawBase64 = (dataUrl: string | null): string | null => {
    if (!dataUrl) return null;
    return dataUrl.split(';base64,').pop() ?? null;
  };

  /**
   * Salva un file sul dispositivo, in base agli header restituiti dal server.
   * Da usare passando la risposta del server, usando **observeResponse = true** come option nel metodo exec
   * @param {Response} response - La response del server
   */
  export const download = (response: HttpResponse<Blob>) => {
    const cdHeader = response.headers.get('Content-Disposition');
    if (!cdHeader) return;
    const [, filename] = cdHeader.match(/filename="(.+)"/)!;
    const blob = new Blob([response.body as any]);
    saveAs(blob, filename);
  };

  /**
   * Parses query params
   */
  export const parseQueryParams = (params: string): GenericObject | null => {
    if (!params) return null;
    return params.split('&').reduce((acc, val) => {
      const [key, value] = val.split('=');
      acc[key] = value.replace(/%7B/g, '{').replace(/%7D/, '}');
      return acc;
    }, {} as GenericObject);
  };

  export const delay = (milliseconds: number) => new Promise((resolve) => setTimeout(resolve, milliseconds));

  /**
   * Ottiene un numero intero casuale compreso tra `min` e `max`.
   *
   * @param {number} min - Il numero minimo
   * @param {number} max - Il numero massimo
   * @return {number} - Il numero casuale
   */
  export const randomBetween = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min;

  /**
   * Esegue una funzione ogni tot secondi, dove tot è un numero casuale compreso tra `min` e `max`.
   *
   * @param {function} intervalFn - La funzione da eseguire
   * @param {number} min - Il numero minimo di secondi tra un'invocazione e la successiva
   * @param {number} max - Il numero massimo di secondi tra un'invocazione e la successiva
   * @return {object} - Un oggetto con metodo `clear()` per interrompere le esecuzioni successive
   */
  export const setRandomInterval = (intervalFn: () => void, min: number, max: number) => {
    let timeout: string | number | NodeJS.Timeout | undefined;

    const runInterval = () => {
      const timeoutFunction = () => {
        intervalFn();
        runInterval();
      };

      const delay = randomBetween(min, max);
      timeout = setTimeout(timeoutFunction, delay);
    };

    runInterval();

    return {
      clear() {
        clearTimeout(timeout);
      },
    };
  };
}
